import { type ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useCurrentUser } from "@/hooks/queries/auth/useCurrentUser";
import { Spinner } from "@/components/common/Spinner";

type Props = { children: ReactNode };

/**
 * Redirects unauthenticated users to the tenant login page.
 * Separate from TenantContextProvider — this only answers "is there a session?".
 * TenantContextProvider answers "does the session's tenant match the URL slug?".
 */
export function RequireAuth({ children }: Props): JSX.Element {
  const { isLoading, isAuthenticated } = useCurrentUser();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const target = tenantSlug ? `/${tenantSlug}/login` : "/login";
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
